import { SUPPORTED_LANGUAGES } from 'sections/client-management/ui/pages/client-settings/supported-languages/constants/SupportedLanguagesConstants';
import { TemplateButton } from '../styles';
import { useCallback } from 'react';
import { TEMPLATE_STATUS_COLOURS } from 'sections/design/template-management/constants/constants';
import { TemplateWarningIcon } from '../../../template-editor/components/TemplateWarningIcon';
import { useTranslation } from 'react-i18next';
import { Button } from '@salecycle/ui-component-library';
import styled from 'styled-components';
import { useTemplatesListState } from '../../context/UseTemplatesListContext';

export interface DetermineLinkButtonProps {
  language: string;
  handleEditClick(language: string): void;
  handleLanguageClicked(language: string, friendlyLanguageName: string): void;
}

const ButtonInner = styled.span`
  display: inline-block;
  min-width: 90px;
`;

export const DetermineLinkButton = ({ language, handleEditClick, handleLanguageClicked }: DetermineLinkButtonProps) => {
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages', 'templates']);
  const { selectedTemplate, supportedLanguages } = useTemplatesListState();

  const languageName = SUPPORTED_LANGUAGES.find(lan => lan.id === language)?.name ?? language;
  const languageStatusIndex = selectedTemplate.supportedLanguages?.findIndex(lan => lan.language === language);

  const onHandleEditClick = useCallback(() => {
    handleEditClick(selectedTemplate.supportedLanguages[languageStatusIndex].language);
  }, [handleEditClick, languageStatusIndex, selectedTemplate.supportedLanguages]);

  const onLanguageClicked = useCallback(() => {
    handleLanguageClicked(language, t(languageName));
  }, [language, handleLanguageClicked, languageName, t]);

  if (languageStatusIndex > -1) {
    const status = selectedTemplate.supportedLanguages[languageStatusIndex].status;
    const templateStatusKey = `templates:templateStatus.${status.toLowerCase()}`;

    const isLanguageSupported = supportedLanguages.includes(language);

    return (
      <TemplateButton
        onClick={onHandleEditClick}
        key={`editButton.${selectedTemplate.supportedLanguages[languageStatusIndex].language}`}
        backgroundColor={TEMPLATE_STATUS_COLOURS[status]}
        role="Button"
      >
        {`${t(languageName)}`} ({`${t(`${templateStatusKey}`)}`}){!isLanguageSupported && <TemplateWarningIcon />}
      </TemplateButton>
    );
  }

  return (
    <Button
      onClick={onLanguageClicked}
      variation="primary"
      ariaLabel={`${t('common.create')} ${languageName.toUpperCase()}`}
      key={`createButton.${languageName}`}
      width={236}
    >
      <ButtonInner>{`${t(languageName)}`}</ButtonInner>
    </Button>
  );
};
